'use client';

import React, {useEffect, useRef} from 'react';
import {cn} from '@/lib/utils';
import {PortableText} from '@portabletext/react';
import {customRichTextComponent} from '@/sanity/sanityPortableText';
import {PortableTextBlock} from 'sanity';
import Image from 'next/image';
import {GROQImage} from '@/sanity/lib/types';
import gsap from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

import whatsappBg from '@/public/global/whatsapp-bg.png';
import attachment from '@/public/icons/Attachment.svg';
import paperClip from '@/public/icons/Paper-clip.svg';
import camera from '@/public/icons/Camera.svg';
import mic from '@/public/icons/Mic-fill.svg';

export default function LastCTA({
  messages,
  className = '',
}: {
  messages: {
    messageBy: string;
    messageText: PortableTextBlock[];
    messageImage?: GROQImage;
    messageTime: string;
  }[];
  className?: string;
}) {
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);

  // scroll trigger message reveal
  useEffect(() => {
    if (!outerContainerRef.current || !scrollContainerRef.current || messageRefs.current.length === 0) return;

    const ctx = gsap.context(() => {
      const messageCount = messages.length;

      messageRefs.current.forEach((messageEl, index) => {
        if (!messageEl) return;

        // messages appear in first 60% of scroll, leaving 40% as buffer
        const scrollRange = 60;
        const progressStart = (index / messageCount) * scrollRange;
        const progressEnd = ((index + 1) / messageCount) * scrollRange;

        ScrollTrigger.create({
          trigger: outerContainerRef.current,
          start: `top+=${progressStart}% top`,
          end: `top+=${progressEnd}% top`,
          onEnter: () => {
            const tl = gsap.timeline();

            tl.to(messageEl, {
              opacity: 1,
              y: 0,
              duration: 0.25,
              ease: 'power2.out',
            }, 0);

            // scroll message into view
            if (scrollContainerRef.current && messageEl) {
              const container = scrollContainerRef.current;
              const messageBottom = messageEl.offsetTop + messageEl.offsetHeight;
              const containerHeight = container.clientHeight;
              const targetScrollTop = messageBottom - containerHeight;

              if (targetScrollTop > container.scrollTop) {
                tl.to(container, {
                  scrollTop: targetScrollTop,
                  duration: 0.25,
                  ease: 'power2.out',
                }, 0);
              }
            }
          },
          onLeaveBack: () => {
            gsap.to(messageEl, {
              opacity: 0,
              y: 24,
              duration: 0.25,
              ease: 'power2.out',
            });

            if (scrollContainerRef.current && index > 0) {
              const container = scrollContainerRef.current;
              const prevMessage = messageRefs.current[index - 1];

              if (prevMessage) {
                const prevMessageBottom = prevMessage.offsetTop + prevMessage.offsetHeight;
                const containerHeight = container.clientHeight;
                const targetScrollTop = Math.max(0, prevMessageBottom - containerHeight);

                gsap.to(container, {
                  scrollTop: targetScrollTop,
                  duration: 0.25,
                  ease: 'power2.out',
                });
              }
            } else if (index === 0 && scrollContainerRef.current) {
              gsap.to(scrollContainerRef.current, {
                scrollTop: 0,
                duration: 0.25,
                ease: 'power2.out',
              });
            }
          },
        });
      });
    }, outerContainerRef);

    return () => ctx.revert();
  }, [messages]);

  const extractText = (blocks: PortableTextBlock[]) =>
    blocks.map((block) => {
      if (block._type === 'block' && (block as any).children && Array.isArray((block as any).children)) {
        return (block as any).children.map((child: any) => child.text || '').join('');
      }
      return '';
    }).join(' ');

  const getMessageWidth = (blocks: PortableTextBlock[]) => {
    const text = extractText(blocks);
    const length = text.length;
    if (length < 40) return 'w-[31.25rem] lg:w-[37.625rem]';
    if (length < 50) return 'w-[40.125rem] lg:w-[53.75rem]';
    if (length < 100) return 'w-[40.125rem] lg:w-[56.75rem]';
    return 'w-[40.125rem] lg:w-[64rem]';
  };

  const isEmojiOnly = (blocks: PortableTextBlock[]) => {
    let hasInlineImage = false;
    let hasText = false;
    blocks.forEach((block) => {
      if (block._type === 'block') {
        const markDefs = (block as any).markDefs || [];
        const hasInlineImageDef = markDefs.some((def: any) => def._type === 'inlineImage');
        if (hasInlineImageDef) hasInlineImage = true;
        if ((block as any).children && Array.isArray((block as any).children)) {
          (block as any).children.forEach((child: any) => {
            if (child.text && child.text.trim().length > 0) hasText = true;
          });
        }
      }
    });
    return hasInlineImage && !hasText;
  };

  const formatTime = (datetime: string) => {
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getSenderName = (messageBy: string) => {
    return messageBy === 'person1' ? 'Attendee' : 'Bastian';
  };

  return (
    <div
      ref={outerContainerRef}
      className={cn('relative overflow-clip', className)}
      style={{
        height: `${messages.length * 30}vh`
      }}
    >
      <div className="h-full relative w-[min(111rem,calc(100%-4rem))] mx-auto flex flex-col items-center">
        <div className="w-full sticky top-0 h-screen flex flex-col pb-[9rem]">
          <Image
            className="absolute -left-[7rem] max-w-[unset] !w-[124.5rem] !min-w-full !h-[100vh] object-cover pointer-events-none"
            src={whatsappBg}
            alt=""
            priority
          />
          {/* messages viewport */}
          <div
            ref={scrollContainerRef}
            className="relative w-full flex flex-col gap-[1.5rem] grow overflow-y-hidden pt-[4.5rem] lg:pt-[6rem]"
          >
            <div className="self-center flex-shrink-0 bg-white rounded-[1rem] shadow-[0_1.3px_0_rgba(98,98,98,.2),0_-1.3px_0_#EEEEF4] mb-[1.5rem] lg:mb-[2.5rem]">
              <p className="mb-0 leading-none text-[#8E8E93] font-semibold px-[1.5rem] lg:px-[2rem] py-[.875rem] lg:py-[1.25rem] text-[1.625rem] lg:text-[2rem]">
                Today
              </p>
            </div>

            {/* list of messages */}
            <div className="contents">
              {messages.map((message, index) => {
                const isSender = message.messageBy === 'person1';
                const emojiOnly = isEmojiOnly(message.messageText);
                const lastBlock = message.messageText[message.messageText.length - 1];

                // check if last message in chain by same person
                const isLastInChain = index === messages.length - 1 || messages[index + 1]?.messageBy !== message.messageBy;

                return (
                  <div
                    key={index}
                    ref={el => {
                      (messageRefs.current[index] = el);
                    }}
                    className="flex-shrink-0 flex flex-col gap-[.5rem] lg:gap-[1rem] opacity-0"
                    style={{ transform: 'translateY(24px)' }}
                  >
                    <div
                      className={cn(
                        'p-[.5rem] rounded-[1.5rem] lg:rounded-[2rem] relative gradient-border-white-2 before:opacity-50 before:rotate-180',
                        message.messageImage
                          ? '!w-[36.5rem] lg:!w-[45.625rem]'
                          : getMessageWidth(message.messageText),
                        isSender
                          ? 'bg-white self-start'
                          : 'bg-[#D9F9B9] self-end'
                      )}
                    >
                      {message?.messageImage && (
                        <Image
                          className="!w-full rounded-[1.5rem]"
                          src={message.messageImage}
                          alt=""
                        />
                      )}

                      <div className={cn(
                        'richtext richtext-start px-[1rem] py-[.5rem] lg:px-[1.5rem] lg:py-[.75rem] prose-strong:!text-black prose-strong:font-medium prose-p:text-[2.375rem] lg:prose-p:text-[3rem] prose-p:leading-[1.3]',
                        'prose-img:!m-0 prose-img:!inline-block prose-img:-translate-y-[.125rem]',
                        emojiOnly
                          ? 'prose-img:!w-[7rem] lg:prose-img:!w-[9rem]'
                          : 'prose-img:!w-[2.75rem] lg:prose-img:!w-[3.5rem]',
                        isSender
                          ? 'prose-p:text-black'
                          : 'prose-p:text-black/90'
                      )}>
                        <PortableText
                          value={message.messageText}
                          components={customRichTextComponent({
                            block: {
                              normal: ({ children, value }) => {
                                const isLastBlock = value === lastBlock;
                                return (
                                  <p className="w-full flex items-end flex-wrap">
                                    <span>{children}</span>
                                    {isLastBlock && (
                                      <span className="-mb-[.5rem] lg:-mt-[.5rem] text-black/[.25] text-[1.625rem] lg:text-[2rem] font-medium ml-auto inline-block whitespace-nowrap">
                                        {formatTime(message.messageTime)}
                                      </span>
                                    )}
                                  </p>
                                );
                              },
                            },
                          })}
                        />
                      </div>
                    </div>
                    {isLastInChain && (
                      <p className={cn(
                        'mb-0 text-[1.5rem] lg:text-[1.625rem] text-black/80 leading-none uppercase',
                        isSender ? 'self-start pl-[.5rem]' : 'self-end pr-[.5rem]'
                      )}>
                        {getSenderName(message.messageBy)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* chat input */}
          <div className="relative flex-shrink-0 w-full flex gap-[1.5rem] lg:gap-[2rem] mt-[2.5rem] lg:mt-[5rem]">
            <div className="grow bg-white rounded-full h-[6.5rem] lg:h-[8rem] flex items-center justify-between px-[3rem] lg:px-[4rem]">
              <div className="flex items-center gap-[2rem] lg:gap-[2.75rem]">
                <Image
                  className="!w-[4rem] lg:!w-[5rem]"
                  src={attachment}
                  alt="Attachment icon"
                />
                <p className="mb-0 text-[#8E8E93] text-[2.375rem] lg:text-[3rem] leading-none">Message</p>
              </div>

              <div className="flex items-center gap-[2rem] lg:gap-[2.75rem]">
                <Image
                  className="!w-[4rem] lg:!w-[5rem]"
                  src={paperClip}
                  alt="Paperclip icon"
                />
                <Image
                  className="!w-[3.75rem] lg:!w-[4.5rem]"
                  src={camera}
                  alt="Camera icon"
                />
              </div>
            </div>

            <div className="flex-shrink-0 w-[6.5rem] h-[6.5rem] lg:w-[8rem] lg:h-[8rem] bg-[#D9F9B9] rounded-full flex items-center justify-center">
              <Image
                className="!w-[3.25rem]"
                src={mic}
                alt="Microphone icon"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
